import { useEffect, useRef, useState } from "react";
import { useHistoryManager } from "../history/useHistoryManager";
import { useAppSelector } from "../slice/hooks";
import { TransactionCard } from "./transaction-Card";
import { getActiveUser } from "../slice/auth-slice";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "../../lib/context/auth-context";
import { TransactionDetail } from "./transaction-detail";
import {
  buildQueryString,
  extractInfoFromUrl,
  getLoggedinUser,
} from "../../lib/util";
import Pagination from "../common/Pagination";
import Skeleton from "react-loading-skeleton";

export const OrderSkeleton = ({ count = 3 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="transaction-skeleton">
        <div className="transaction-skeleton-textbox">
          <div style={{ width: "50%" }}>
            <Skeleton width="100%" height={20} style={{ marginBottom: 10 }} />
            <Skeleton width="100%" height={20} style={{ marginBottom: 10 }} />
            <Skeleton width="100%" height={20} style={{ marginBottom: 10 }} />
            <Skeleton width="100%" height={20} />
          </div>
          <div style={{ width: "15%" }}>
            <Skeleton width="100%" height={30} />
          </div>
        </div>
        <div className="transaction-skeleton-imagebox">
          {Array.from({ length: 2 }).map((__, j) => (
            <Skeleton key={j} width={50} height={50} />
          ))}
        </div>
      </div>
    ))}
  </>
);

export const TransactionHistory = ({
  accessPointId,
  organisation,
  storePath,
  parentScrollRef,
}) => {
  const client = useClient();
  const { navigateTo } = useHistoryManager(storePath);

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );

  const [isMobile, setIsMobile] = useState(false);
  const scrollRef = useRef(null);
  const [savedScroll, setSavedScroll] = useState(0);

  // Helper to get current scrollable container
  const getScrollElement = () => {
    if (isMobile && parentScrollRef?.current) return parentScrollRef.current;
    return scrollRef.current;
  };

  // Initial URL state
  const initialUrlPage = extractInfoFromUrl("page");
  const initialCollectionId = extractInfoFromUrl("collectionId");

  // State for list view
  const [state, setState] = useState({
    data: [],
    limit: extractInfoFromUrl("limit")
      ? Number(extractInfoFromUrl("limit"))
      : 8,
    total: 0,
    page: initialUrlPage ? Number(initialUrlPage) : 1,
    paymentStatus: extractInfoFromUrl("paymentStatus") || "",
    searchTerm: "",
  });

  const [collection, setCollection] = useState(null);
  const [pageState, setTransactionPageState] = useState(
    initialCollectionId ? "singleTransaction" : "transactionList",
  );

  const activeCollectionId = collection?.collectionId || initialCollectionId;

  const paymentStatusLabels = {
    PAID: { label: "Paid", bg: "#15803d" },
    PARTIAL: { label: "Partially paid", bg: "#00a5ee" },
    PENDING: { label: "Pending", bg: "#e5a600" },
    CANCELLED: { label: "Cancelled", bg: "#d45151" },
  };

  // Fetch transactions list
  const {
    data: transactions = {},
    isLoading,
    isFetching,
    error,
  } = useQuery(
    [
      "transactions",
      state.paymentStatus,
      state.searchTerm,
      state.page,
      state.limit,
      accessPointId,
      loggedInUser?.user?.userId,
      storePath,
    ],
    async () => {
      const queryParams = [
        state.paymentStatus && `paymentStatus=${state.paymentStatus}`,
        state.searchTerm && `searchTerm=${state.searchTerm}`,
        `limit=${state.limit}`,
        `page=${state.page}`,
        storePath !== "market-place" && `accessPointId=${accessPointId}`,
      ].filter(Boolean);

      const userId = loggedInUser?.user?.userId;
      const orgId = organisation?.orgId;
      const baseUrl =
        storePath === "market-place"
          ? `collection/user/${userId}/orders`
          : `collection/organisation/${orgId}/user/${userId}/orders`;

      return client(`${baseUrl}?${queryParams.join("&")}`).then(
        (res) => res.data,
      );
    },
    {
      enabled:
        !!loggedInUser?.user?.userId &&
        (storePath === "market-place" ||
          (!!organisation?.orgId && !!accessPointId)) &&
        pageState === "transactionList",
      keepPreviousData: true,
    },
  );

  // Fetch single transaction details
  const {
    data: singleTransaction = {},
    isLoading: isLoadingSingleTransaction,
    error: singleTransactionError,
  } = useQuery(
    ["transaction", activeCollectionId],
    async () =>
      client(`collection/${activeCollectionId}/info`).then((res) => res.data),
    {
      enabled: pageState === "singleTransaction" && !!activeCollectionId,
    },
  );

  useEffect(() => {
    if (transactions && transactions.collections) {
      setState((prev) => ({
        ...prev,
        data: transactions.collections || [],
        total: transactions.total || 0,
      }));
    }
  }, [transactions]);

  useEffect(() => {
    if (singleTransaction?.collection) {
      setCollection(singleTransaction.collection);
    }
  }, [singleTransaction]);

  // Handlers

  const scrollToTop = () => {
    const el = getScrollElement();
    if (el) el.scrollTop = 0;
  };

  const restoreScroll = () => {
    const el = getScrollElement();
    if (el) el.scrollTop = savedScroll;
  };

  const handleViewMore = (collection) => {
    if (getScrollElement()) {
      setSavedScroll(getScrollElement().scrollTop);
    }
    const query = buildQueryString({ collectionId: collection.collectionId });
    navigateTo(`profile/orders${query}`, {
      urlData: { subPath: "orders", collectionId: collection.collectionId },
      replace: true,
      onNavigate: (navigate) => {
        if (navigate) {
          setTransactionPageState("singleTransaction");
          setCollection(collection);
          scrollToTop();
        }
      },
    });
  };

  const backButton = () => {
    const query = buildQueryString({ collectionId: "" });
    navigateTo(`profile/orders${query}`, {
      urlData: {
        subPath: "orders",
        page: state.page,
        status: state.paymentStatus,
      },
      replace: true,
      onNavigate: (navigate) => {
        if (navigate) {
          setCollection(null);
          setTransactionPageState("transactionList");
          requestAnimationFrame(() => {
            restoreScroll();
          });
        }
      },
    });
  };

  const openPage = (page) => {
    const query = buildQueryString({ page });
    navigateTo(`profile/orders${query}`, {
      urlData: { subPath: "orders", page, status: state.paymentStatus },
      replace: true,
      onNavigate: (navigate) => {
        if (navigate) {
          setState((prev) => ({ ...prev, page }));
          requestAnimationFrame(() => {
            scrollToTop();
          });
        }
      },
    });
  };

  const openPaymentByStatus = (paymentStatus) => {
    const page = 1;
    const query = buildQueryString({ page, paymentStatus });
    navigateTo(`profile/orders${query}`, {
      urlData: { subPath: "orders", page, status: paymentStatus },
      replace: true,
      onNavigate: (navigate) => {
        if (navigate) {
          setState((prev) => ({ ...prev, total: 0, page, paymentStatus }));
        }
      },
    });
  };

  const changeLimit = (newLimit) => {
    const query = buildQueryString({ page: 1, limit: newLimit });
    navigateTo(`profile/orders${query}`, {
      urlData: {
        subPath: "orders",
        page: 1,
        status: state.paymentStatus,
        limit: newLimit,
      },
      replace: true,
      onNavigate: (navigate) => {
        if (navigate) {
          setState((prev) => ({
            ...prev,
            page: 1,
            total: 0,
            limit: newLimit,
          }));
        }
      },
    });
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 500);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <div className="transaction-container">
      {error ? (
        <>
          <div className="nav-header">
            <div className="nav-header-box">
              <h3>Order History</h3>
            </div>
          </div>
          <div className="transaction-tabs">
            <p className="error-text">Failed to load transactions.</p>
          </div>
        </>
      ) : (
        pageState === "transactionList" && (
          <>
            <div className="nav-header">
              <div className="nav-header-box">
                <h3>Order History</h3>
                {isFetching && !isLoading && <span className="only-spinner" />}
              </div>
              <select
                className="transaction-filter"
                value={state.paymentStatus}
                onChange={(e) => openPaymentByStatus(e.target.value)}
              >
                <option value="">All orders</option>
                <option value="PAID">Paid</option>
                <option value="PARTIAL">Partially Paid</option>
                <option value="PENDING">Pending</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            <div className="transaction-tabs" ref={scrollRef}>
              {isLoading ? (
                <OrderSkeleton count={4} />
              ) : state.data.length === 0 ? (
                <p>No Order Found</p>
              ) : (
                state.data.map((tx) => (
                  <TransactionCard
                    key={tx.checkSum}
                    transactions={tx}
                    handleViewMore={handleViewMore}
                    paymentStatusLabels={paymentStatusLabels}
                    storePath={storePath}
                  />
                ))
              )}
            </div>

            <Pagination
              infiniteScroll={false}
              total={state.total}
              limit={state.limit}
              page={state.page}
              onPageClick={openPage}
              onLimitChange={changeLimit}
              changeLimit={true}
              isLoading={isLoading}
              isFetching={isFetching}
              scrollContainerRefCurrent={getScrollElement()}
            />
          </>
        )
      )}

      {pageState === "singleTransaction" && (
        <TransactionDetail
          singleTransactionError={singleTransactionError}
          collection={collection}
          isLoadingSingleTransaction={isLoadingSingleTransaction}
          backButton={backButton}
          paymentStatusLabels={paymentStatusLabels}
        />
      )}
    </div>
  );
};
